import React, { useState, useEffect, useMemo } from 'react';
import { Search, Plus, Minus } from 'lucide-react';
import FormControl from '../common/FormControl';
import Button from '../common/Button';
import LocationList from './LocationList';
import LocationForm from './LocationForm';
import { Location } from './LocationTypes';
import { orgService } from '../../services/orgService';
import { geographyTypeService, GeographyType } from '../../services/geographyTypeService';
import { useAuth } from '../../contexts/AuthContext';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmWarning, confirmActive } from '../common/confirm';
import WorkflowModal from '../common/WorkflowModal';
import { usePermission } from '../../contexts/PermissionContext';
import NoDataFound from '../common/NoDataFound';
import StatusSwitch from '../common/StatusSwitch';
import extractApiError from '../../utils/apiError';

const RBAC_ACTIVITY = 'ORG_GEOGRAPHIES';

type WorkflowState = 'draft' | 'submitted' | 'approved' | 'rejected';
type WorkflowFields = {
    workflow_state: WorkflowState;
    submitted_by?: any; submitted_on?: any;
    approved_by?: any; approved_on?: any; approval_note?: any;
    rejected_by?: any; rejected_on?: any; rejection_reason?: any;
};
type LocationWithWorkflow = Location & WorkflowFields;

interface LocationManagementProps {
    compact?: boolean;
}

const LocationManagement: React.FC<LocationManagementProps> = ({ compact = false }) => {
    const { state } = useAuth();

    const viewPerm = usePermission(RBAC_ACTIVITY, 'view');
    const modifyPerm = usePermission(RBAC_ACTIVITY, 'view+modify');
    const canView = viewPerm.allowed;
    const canModify = modifyPerm.allowed;
    const permsLoading = viewPerm.loading;

    const [locations, setLocations] = useState<LocationWithWorkflow[]>([]);
    const [geographyTypes, setGeographyTypes] = useState<GeographyType[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingLocation, setEditingLocation] = useState<LocationWithWorkflow | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [toggling, setToggling] = useState(false);
    const [workflowOpenFor, setWorkflowOpenFor] = useState<LocationWithWorkflow | null>(null);

    const fetchLocations = async () => {
        if (!canView) return;
        try {
            setLoading(true);
            const response = await orgService.getLocations(1, 100);
            const mappedLocations: LocationWithWorkflow[] = response.data.map((item: any) => ({
                id: String(item.id),
                name: item.name,
                type: item.is_physical ? 'PHYSICAL' : 'ABSTRACT',
                establishedDate: item.installation_date,
                parentId: item.parent_location_id ? String(item.parent_location_id) : 'root',
                geography_type_id: item.geography_type_id ?? null,
                address: item.address,
                description: '',
                entity_id: item.entity_id,
                is_active: item.is_active,
                code: item.code,
                code_seq: item.code_seq,
                workflow_state: (item.workflow_state ?? 'draft') as WorkflowState,
                submitted_by: item.submitted_by,
                submitted_on: item.submitted_on,
                approved_by: item.approved_by,
                approved_on: item.approved_on,
                approval_note: item.approval_note,
                rejected_by: item.rejected_by,
                rejected_on: item.rejected_on,
                rejection_reason: item.rejection_reason,
            }));
            setLocations(mappedLocations);
        } catch (err) {
            console.error('Failed to fetch geographies:', err);
            setToast({ type: 'error', message: 'Failed to load geographies' });
        } finally {
            setLoading(false);
        }
    };

    const fetchGeographyTypes = async () => {
        try {
            const items = await geographyTypeService.dropdown();
            setGeographyTypes(items);
        } catch (err) {
            console.error('Failed to fetch geography types:', err);
            setGeographyTypes([]);
        }
    };

    useEffect(() => {
        if (canView) {
            fetchLocations();
            fetchGeographyTypes();
        }
    }, [canView]);

    const parentIds = useMemo(() => {
        const ids = new Set<string>();
        const parents = new Set(
            locations
                .map(l => l.parentId)
                .filter((p): p is string => p != null && p !== 'root'),
        );
        locations.forEach(l => { if (parents.has(l.id)) ids.add(l.id); });
        return ids;
    }, [locations]);

    const handleToggleExpand = (id: string) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const isAllExpanded = parentIds.size > 0 && expandedIds.size >= parentIds.size;

    const handleToggleAll = () => {
        if (isAllExpanded) setExpandedIds(new Set());
        else setExpandedIds(new Set(parentIds));
    };

    const filteredLocations = useMemo(() => {
        const q = searchTerm.toLowerCase();
        return locations.filter(l => {
            const matchesStatus =
                statusFilter === 'ACTIVE' ? l.is_active === 1 : l.is_active !== 1;
            const matchesSearch =
                !q ||
                l.name.toLowerCase().includes(q) ||
                (l.description && l.description.toLowerCase().includes(q)) ||
                (l.address && l.address.toLowerCase().includes(q));
            return matchesStatus && matchesSearch;
        });
    }, [locations, searchTerm, statusFilter]);

    const handleOpenAdd = () => {
        if (!canModify) return;
        setEditingLocation(null);
        setIsModalOpen(true);
    };

    const handleOpenEdit = (location: Location) => {
        setEditingLocation({ ...location, workflow_state: (location.workflow_state ?? 'draft') as WorkflowState } as LocationWithWorkflow);
        setIsModalOpen(true);
    };

    const handleToggleActive = async () => {
        if (!editingLocation || !canModify) return;
        if (editingLocation.workflow_state === 'approved') {
            setToast({ type: 'warning', message: 'Record is approved and locked.' });
            return;
        }
        const next = editingLocation.is_active ? 0 : 1;
        const isActive = editingLocation.is_active;
        const confirmFn = isActive ? confirmWarning : confirmActive;
        const ok = await confirmFn({
            title: next ? 'Reactivate this geography?' : 'Inactivate this geography?',
            text: next ? 'It will appear in active lists again.' : 'It will be hidden from active lists.',
            confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
        });
        if (!ok) return;
        setToggling(true);
        try {
            await orgService.updateLocation(editingLocation.id, { is_active: next } as any);
            setToast({ type: next ? 'success' : 'warning', message: next ? 'Geography reactivated.' : 'Geography inactivated.' });
            await fetchLocations();
        } catch (err: any) {
            setToast({ type: 'error', message: extractApiError(err, 'Failed to update status.') });
        } finally {
            setToggling(false);
        }
    };

    const handleSubmit = async (data: Omit<Location, 'id'>) => {
        try {
            const payload = {
                entity_id: state.user?.entity_id || 1,
                name: data.name,
                parent_location_id: data.parentId === 'root' ? null : Number(data.parentId),
                geography_type_id: data.geography_type_id ?? null,
                installation_date: data.establishedDate,
                is_physical: data.type === 'PHYSICAL',
                address: data.address,
                description: data.description || null,
                created_by: state.user?.id || 1,
            };

            if (editingLocation) {
                await orgService.updateLocation(editingLocation.id, {
                    ...payload,
                    modified_by: state.user?.id || 1,
                } as any);
                await fetchLocations();
                setToast({ type: 'success', message: 'Geography updated successfully.' });
            } else {
                await orgService.createLocation(payload as any);
                await fetchLocations();
                setToast({ type: 'success', message: 'Geography created successfully.' });
            }
            setIsModalOpen(false);
        } catch (err: any) {
            console.error('Failed to save geography:', err);
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === 'string' ? detail : (detail ? JSON.stringify(detail) : err?.message);
            setToast({ type: 'error', message: msg || 'Failed to save geography.' });
            throw err;
        }
    };

    const closeWfModal = () => setWorkflowOpenFor(null);

    if (!permsLoading && !canView) {
        return (
            <NoDataFound
                title="Not authorised"
                message={viewPerm.message || 'You are not authorised to view Geographies. Please contact your Super Admin.'}
            />
        );
    }

    return (
        <>
        <Toast toast={toast} onClose={() => setToast(null)} />
        <div className={`netbody-card flex flex-col h-full ${compact ? '' : 'min-h-[500px]'}`}>

            <div className="netbody-header">
                {/* Status — iOS-style on/off switch (on = active) */}
                <StatusSwitch
                    value={statusFilter === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE'}
                    onChange={next => setStatusFilter(next)}
                    noun="geographies"
                    testId="SEL_location_status_filter"
                />
                <Button
                    variant="secondary-outline"
                    icon={isAllExpanded ? Minus : Plus}
                    onClick={handleToggleAll}
                    data-testid="BTN_location_mgmt_expand_all"
                >
                    {isAllExpanded ? 'Collapse All' : 'Expand All'}
                </Button>
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search geographies..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_location_search"
                    />
                    {canModify && (
                        <Button variant="fill" icon={Plus} onClick={handleOpenAdd} data-testid="BTN_location_mgmt_create">
                            Create
                        </Button>
                    )}
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <LocationList
                        locations={filteredLocations}
                        loading={loading}
                        onEdit={handleOpenEdit}
                        expandedIds={expandedIds}
                        onToggleExpand={handleToggleExpand}
                        geographyTypes={geographyTypes}
                        onWorkflow={(l: Location) => setWorkflowOpenFor({ ...l, workflow_state: (l.workflow_state ?? 'draft') as WorkflowState } as LocationWithWorkflow)}
                    />
                </div>
            </div>

            <LocationForm
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleSubmit}
                initialData={editingLocation}
                locations={locations}
                geographyTypes={geographyTypes}
                onToggleActive={editingLocation && canModify ? handleToggleActive : undefined}
                toggling={toggling}
                canModify={canModify}
                isApproved={editingLocation?.workflow_state === 'approved'}
            />
        </div>

        <WorkflowModal
            isOpen={!!workflowOpenFor}
            onClose={closeWfModal}
            kind="org_location"
            id={workflowOpenFor ? Number(workflowOpenFor.id) : null}
            onChanged={fetchLocations}
        />
        </>
    );
};

export default LocationManagement;
